'use es6';

import { cloneElement } from 'react';
import { Range, List } from 'immutable';
import flattenChildren from './flattenChildren';
import { isBefore, isAfter, isSunday, getCachedMoment, getDayOfWeek, compare, diff, isSame, getHour, eventsOverlap } from './time';

/**
 * @deprecated This is a legacy method that sorts events based on React component props (and not actual calendar events). This method is still being used for historical reasons.
 *
 * Whenever making updates to this method, make sure to also update `sortCalendarEvents` in `ui-addon-calendars/static-2.4254/js/lib/eventList.ts`.
 */
export function getSortedChildren(children, useExactEventTimes, timezone) {
  const childrenArray = flattenChildren(children);
  const sortedChildren = List(childrenArray).sort((eventA, eventB) => {
    const {
      end: endA,
      start: startA,
      allDay: aAllDay
    } = eventA.props;
    const {
      end: endB,
      start: startB,
      allDay: bAllDay
    } = eventB.props;
    if (!(aAllDay && bAllDay) && (aAllDay || bAllDay) && isSame(startA, startB, timezone, 'day')) {
      return aAllDay ? -1 : 1;
    }
    let eventALength;
    let eventBLength;
    if (aAllDay && bAllDay) {
      eventALength = diff(endA, startA, 'days') + 1;
      eventBLength = diff(endB, startB, 'days') + 1;
    } else {
      eventALength = diff(endA, startA, 'hours');
      eventBLength = diff(endB, startB, 'hours');
    }
    if (!isSame(startA, startB, timezone, 'day')) {
      return compare(startA, startB, timezone, 'day');
    }
    if (eventALength !== eventBLength) {
      if (eventALength > eventBLength) return -1;
      if (eventALength < eventBLength) return 1;
    }
    return compare(startA, startB, timezone, 'minute');
  });
  return sortedChildren.map((child, index) => {
    const {
      start,
      end,
      allDay
    } = child.props;
    return /*#__PURE__*/cloneElement(child, {
      id: index,
      length: diff(end, start, 'hour', useExactEventTimes) || 1,
      allDay: allDay || diff(end, start, 'hours') >= 24
    });
  });
}
function getEventIndex(events) {
  const index = events.findIndex(event => {
    return !event;
  });
  return index !== -1 ? index : events.size;
}

/**
 * @deprecated This is a legacy method that uses events based on React component props (and not actual calendar events). This method is still being used for historical reasons.
 *
 * Whenever making updates to this method, make sure to also update `distributeEventsToWeekDays` in `ui-addon-calendars/static-2.4254/js/lib/eventList.ts`.
 */
export function distributeEventsToWeekDays(sortedChildren, weekStartDate, weekEndDate, timezone, hideWeekends = false) {
  const doesWeekStartOnSunday = isSunday(weekStartDate);
  return sortedChildren.reduce((accumulator, child) => {
    const {
      start,
      end: endRaw,
      allDay
    } = child.props;

    // ensure single-day events don't render on next day (e.g. 1hr event starting at 23:30)
    const end = allDay ? endRaw : start;
    if (!start || isBefore(end, weekStartDate, timezone) || isAfter(start, weekEndDate, timezone)) {
      return accumulator;
    }
    const visibleStart = isBefore(start, weekStartDate, timezone) ? weekStartDate : start;
    const visibleEnd = isBefore(end, weekEndDate, timezone) ? end : weekEndDate;
    const eventStartWeekDay = getDayOfWeek(visibleStart, timezone);
    let eventsForWeekDay = accumulator[eventStartWeekDay];
    const eventDuration = diff(getCachedMoment(visibleEnd, timezone), getCachedMoment(visibleStart, timezone), 'days') + 1;
    const rootEventIndex = getEventIndex(eventsForWeekDay);
    const event = /*#__PURE__*/cloneElement(child, {
      length: eventDuration
    });
    if (eventDuration > 1) {
      const lastDayOfEventThisWeek = eventStartWeekDay + eventDuration;
      Range(eventStartWeekDay + 1, lastDayOfEventThisWeek).forEach((placeholderDayOfWeek, index) => {
        const shouldAddPlaceholder = !(index === 0 && doesWeekStartOnSunday && hideWeekends);
        const events = accumulator[placeholderDayOfWeek];
        const placeholderElement = /*#__PURE__*/cloneElement(child, {
          placeholder: shouldAddPlaceholder
        });
        if (events.size <= rootEventIndex) {
          accumulator[placeholderDayOfWeek] = events.set(rootEventIndex, placeholderElement);
        } else {
          accumulator[placeholderDayOfWeek] = events.insert(rootEventIndex, placeholderElement);
        }
      });
    }
    eventsForWeekDay = accumulator[eventStartWeekDay];
    if (eventsForWeekDay.size > 0 && !eventsForWeekDay.get(rootEventIndex)) {
      accumulator[eventStartWeekDay] = eventsForWeekDay.set(rootEventIndex, event);
    } else {
      accumulator[eventStartWeekDay] = eventsForWeekDay.insert(rootEventIndex, event);
    }
    return accumulator;
  }, {
    0: List(),
    1: List(),
    2: List(),
    3: List(),
    4: List(),
    5: List(),
    6: List()
  });
}
const getEmptyHourList = Range(0, 24).map(() => List()).toList();

/**
 * @deprecated This is a legacy method that uses events based on React component props (and not actual calendar events). This method is still being used for historical reasons.
 *
 * Whenever making updates to this method, make sure to also update `distributeEventsToHours` in `ui-addon-calendars/static-2.4254/js/lib/eventList.ts`.
 */
export function distributeEventsToHours(childrenForDay, timezone) {
  return childrenForDay.reduce((accumulator, child) => {
    const {
      start,
      end
    } = child.props;
    let eventDuration = diff(end, start, 'hours');
    // we want any events < 1 hour to default to 1 hour length
    eventDuration = eventDuration > 1 ? 1 : eventDuration;
    const startHour = getHour(start, timezone);
    const updatedListForHour = accumulator.get(startHour).push(child);
    accumulator = accumulator.set(startHour, updatedListForHour);
    if (eventDuration > 1) {
      Range(1, eventDuration).forEach(hour => {
        const placeholderElement = /*#__PURE__*/cloneElement(child, {
          placeholder: true
        });
        accumulator = accumulator.get(startHour + hour).push(placeholderElement);
      });
    }
    return accumulator;
  }, getEmptyHourList);
}

// turns a single list of events into a 2D array of events, grouped by an
// overlap with some other event in the group. it doesn't need to overlap with
// all others in the group to be included

export const groupOverlappingEvents = displayEvents => {
  return displayEvents.reduce((_overlapGroups, event) => {
    const overlapGroupIndex = _overlapGroups.findIndex(group => group.some(groupEvent => eventsOverlap(groupEvent, event)));
    if (overlapGroupIndex >= 0) {
      return _overlapGroups.update(overlapGroupIndex, group => group.push(event));
    }
    return _overlapGroups.push(List.of(event));
  }, List());
};
export const getNoUndefinedEvents = events => Object.entries(events).reduce((acc, [dayIndex, dayEvents]) => {
  acc[dayIndex] = dayEvents.filter(Boolean);
  return acc;
}, {
  0: List(),
  1: List(),
  2: List(),
  3: List(),
  4: List(),
  5: List(),
  6: List()
});

/**
 * @deprecated This is a legacy method that uses events based on React component props (and not actual calendar events). This method is still being used for historical reasons.
 *
 * Whenever making updates to this method, make sure to also update `getOverflowsAndEventKeysForWeek` in `ui-addon-calendars/static-2.4254/js/lib/eventList.ts`.
 */
export const getOverflowsAndEventKeysForWeek = ({
  events,
  maxEventDisplayCount
}) => {
  return Object.entries(events).reduce((acc, [dayIndex, dayEvents]) => {
    const overflowCount = dayEvents.size > maxEventDisplayCount ? Math.max(dayEvents.size + 1 - maxEventDisplayCount, 0) : 0;
    const multiDayEvents = [];
    const singleDayEvents = [];
    dayEvents.forEach(event => {
      if (event.props && event.props.length > 1) {
        multiDayEvents.push(event.key);
      } else {
        singleDayEvents.push(event.key);
      }
    });
    acc[dayIndex] = {
      overflowCount,
      multiDayEvents,
      singleDayEvents
    };
    return acc;
  }, {});
};

/**
 * @deprecated This is a legacy method that uses events based on React component props (and not actual calendar events). This method is still being used for historical reasons.
 *
 * Whenever making updates to this method, make sure to also update `getCorrectOrderOfEventsWithMultiDay` in `ui-addon-calendars/static-2.4254/js/lib/eventList.ts`.
 *
 * Re-orders the events to keep only the max multi-day events visible
 * Loops through the events for the week and re-orders the events List
 * sending multi-day events to the end based on:
 * 1. The current day has an overflowCount
 * 2. The multi-day event's index is greater than the maxMultiDayEventDisplayCount
 * @param {Object} events - Object with events per day
 * @param {number} maxEventDisplayCount - The maximum of events to display
 * @param {number} maxMultiDayEventDisplayCount - The maximum of multi-day events to display
 * @returns {Object} Re-ordered events object
 */
export const getCorrectOrderOfEventsWithMultiDay = ({
  events,
  maxEventDisplayCount,
  maxMultiDayEventDisplayCount
}) => {
  const overflowsAndEventKeysForWeek = getOverflowsAndEventKeysForWeek({
    events,
    maxEventDisplayCount
  });
  const multiDayEventsToOverflow = {};
  return Object.entries(events).reduce((acc, [dayIndex, dayEvents]) => {
    const {
      overflowCount,
      multiDayEvents
    } = overflowsAndEventKeysForWeek[dayIndex];
    if (!multiDayEvents.length) {
      acc[dayIndex] = dayEvents;
      return acc;
    }
    let multiDayEventsList = List();
    dayEvents.forEach((event, index) => {
      const isMultiEventOverMaxLimit = index + 1 > maxMultiDayEventDisplayCount && event.props.length > 1;
      if (overflowCount && isMultiEventOverMaxLimit) {
        multiDayEventsToOverflow[event.key] = true;
      }
      if (multiDayEventsToOverflow[event.key]) {
        const finalEvent = Object.assign({}, event, {
          multiDayEventHidden: true
        });
        multiDayEventsList = multiDayEventsList.push(finalEvent);
        dayEvents = dayEvents.set(index, undefined);
      }
    });
    dayEvents = dayEvents.filter(Boolean).concat(multiDayEventsList);
    acc[dayIndex] = dayEvents;
    return acc;
  }, {
    0: List(),
    1: List(),
    2: List(),
    3: List(),
    4: List(),
    5: List(),
    6: List()
  });
};

/**
 * @deprecated This is a legacy method that uses events based on React component props (and not actual calendar events). This method is still being used for historical reasons.
 *
 * Whenever making updates to this method, make sure to also update `getEventsTaggedWithHiddenMultiDayEvents` in `ui-addon-calendars/static-2.4254/js/lib/eventList.ts`.
 *
 * Adds multiDayEventHidden to multi-day events based on next days events.
 * Loops through the events and the next days of the week and checks if
 * multi-day events should be sent to the overflow as they're not able
 * to show their entire length for the week based on:
 * 1. Having a single-day event on the following days
 * 2. Having a overflow of multi-day events on the following days and their index being equal to the maxEventDisplayCount
 * @param {Object} events - Object with events per day
 * @param {number} maxEventDisplayCount - The maximum of events to display
 * @param {number} maxMultiDayEventDisplayCount - The maximum of multi-day events to display
 * @returns {Object} Updated events object
 */
export const getEventsTaggedWithHiddenMultiDayEvents = ({
  events,
  maxEventDisplayCount,
  maxMultiDayEventDisplayCount
}) => {
  const overflowsAndEventKeysForWeek = getOverflowsAndEventKeysForWeek({
    events,
    maxEventDisplayCount
  });
  return Object.entries(events).reduce((acc, [dayIndex, dayEventsRaw]) => {
    let dayEvents = dayEventsRaw;
    dayEvents.forEach((event, index) => {
      const isMultiEventOverMaxLimit = index + 1 > maxMultiDayEventDisplayCount && event.props.length > 1;
      if (isMultiEventOverMaxLimit) {
        for (let dayLeft = Number(dayIndex) + 1; dayLeft < 7; dayLeft++) {
          const {
            overflowCount: dayLeftOverflowCount,
            multiDayEvents: dayLeftMultiDayEvents,
            singleDayEvents: dayLeftSingleDayEvents
          } = overflowsAndEventKeysForWeek[dayLeft];
          if (!event.props.placeholder && dayLeftOverflowCount && (dayLeftSingleDayEvents.length || dayLeftMultiDayEvents.length > maxEventDisplayCount && index + 1 === maxEventDisplayCount) && dayLeftMultiDayEvents.includes(event.key)) {
            const finalEvent = Object.assign({}, event, {
              multiDayEventHidden: true
            });
            dayEvents = dayEvents.set(index, finalEvent);
            break;
          }
        }
      }
    });
    acc[dayIndex] = dayEvents;
    return acc;
  }, {
    0: List(),
    1: List(),
    2: List(),
    3: List(),
    4: List(),
    5: List(),
    6: List()
  });
};